import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { imageApi } from '../../../api/imageApi.js';
import { loginApi } from '../../../api/loginApi.js';
import { NavBar } from '../../app/navBar.js';
import './homeScreen.css';

const MAX_CONCURRENT_LOADS = 3;

const HomeScreen = () => {
    const [imageList, setImageList] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const navigate = useNavigate();
    const observerRef = useRef(null);
    const loadingIdsRef = useRef(new Set());
    const loadedIdsRef = useRef(new Set());
    const objectUrlsRef = useRef(new Map());
    const queuedIdsRef = useRef(new Set());
    const queueRef = useRef([]);
    const activeLoadsRef = useRef(0);
    const imageMapRef = useRef(new Map());

    useEffect(() => {
        const fetchHomepageImages = async () => {
            try {
                const loginResponse = await loginApi.loginRoles();
                if (loginResponse.status === false && loginResponse.msg === "Forbidden") {
                    navigate('/login');
                    return;
                }

                if(loginResponse.data.admin === false) {
                    navigate('/albums/' + loginResponse.data.albumID);
                    return;
                }

                const response = await imageApi.searchHomepage();
                if (response.status === false && response.msg === "Forbidden") {
                    navigate('/login');
                    return;
                }

                if (response && response.data && response.data.ids) {
                    // Initialize images without loading them
                    setImageList(response.data.ids.map(img => ({...img, src: null})));
                }
            } catch (error) {
                if (error.status === 403) {
                    navigate('/login');
                } else {
                    console.error('Error fetching the images:', error);
                }
            }
        };

        fetchHomepageImages();
    }, [navigate]);

    useEffect(() => {
        imageMapRef.current = new Map(imageList.map((img) => [img.id, img]));
    }, [imageList]);

    const loadImage = async (img) => {
        if (!img || loadedIdsRef.current.has(img.id) || loadingIdsRef.current.has(img.id)) {
            return;
        }

        loadingIdsRef.current.add(img.id);
        try {
            const blobUrl = await imageApi.getImageBytes(img.id);
            objectUrlsRef.current.set(img.id, blobUrl);
            loadedIdsRef.current.add(img.id);
            setImageList((prevImages) =>
                prevImages.map((image) =>
                    image.id === img.id ? { ...image, src: blobUrl } : image
                )
            );
        } catch (error) {
            if (error.status === 403) {
                navigate('/login');
            } else {
                console.error(`Error loading image ${img.id}:`, error);
            }
        } finally {
            loadingIdsRef.current.delete(img.id);
            queuedIdsRef.current.delete(img.id);
        }
    };

    const processQueue = () => {
        while (activeLoadsRef.current < MAX_CONCURRENT_LOADS && queueRef.current.length > 0) {
            const nextImage = queueRef.current.shift();
            if (!nextImage) {
                return;
            }

            activeLoadsRef.current += 1;
            loadImage(nextImage).finally(() => {
                activeLoadsRef.current -= 1;
                processQueue();
            });
        }
    };

    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    const imageId = Number(entry.target.dataset.imgId);
                    const imageObj = imageMapRef.current.get(imageId);
                    if (
                        imageObj &&
                        !loadedIdsRef.current.has(imageObj.id) &&
                        !loadingIdsRef.current.has(imageObj.id) &&
                        !queuedIdsRef.current.has(imageObj.id)
                    ) {
                        queuedIdsRef.current.add(imageObj.id);
                        queueRef.current.push(imageObj);
                        processQueue();
                    }

                    observerRef.current.unobserve(entry.target);
                });
            },
            {
                rootMargin: '200px 0px',
                threshold: 0.05
            }
        );

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        if (!observerRef.current) {
            return;
        }

        const images = document.querySelectorAll('.grid-images img[data-img-id]');
        images.forEach((imgNode) => {
            const imageId = Number(imgNode.dataset.imgId);
            if (!loadedIdsRef.current.has(imageId) && !loadingIdsRef.current.has(imageId)) {
                observerRef.current.observe(imgNode);
            }
        });
    }, [imageList]);

    useEffect(() => {
        return () => {
            objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
            objectUrlsRef.current.clear();
        };
    }, []);

    const handleImageClick = (image) => {
        if (!image.src) {
            return;
        }
        setSelectedImage(image);
    };

    const handleCloseClick = () => {
        setSelectedImage(null);
    }

    const handleEditClick = (imageId) => {
        navigate(`/details/${imageId}`);
    }

    return (
        <div>
            <NavBar/>
            <div className="grid-images">
                {imageList.map(image => (
                    <div key={image.id} className="image-tile">
                        <img
                            data-img-id={image.id}
                            src={image.src || undefined}
                            alt={image.title}
                            loading="lazy"
                            decoding="async"
                            onClick={() => handleImageClick(image)}
                            className={image.src ? 'loaded' : 'loading'}
                        />
                    </div>
                ))}
            </div>

            {selectedImage && (
                <div className="modal" onClick={handleCloseClick}>
                    <div className="modal-image-container">
                        <img
                            src={selectedImage.src}
                            alt={selectedImage.title}
                            className="modal-image"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <div className="icon-container">
                            <span className="icon" onClick={() => handleEditClick(selectedImage.id)}>✏️</span>
                            <span className="icon icon-close" onClick={handleCloseClick}>✖️</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export { HomeScreen };